import { useVirtualListScrollRestore } from '@/hooks/use-virtual-list-scroll-restore';
import { useUnit } from 'effector-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import { LogoLoader } from '@/components/logo-loader';
import {
	$addMessages,
	$cursorMessages,
	$hasMoreMessages,
	$isFetchingMessages,
	$messages,
	$setCursorMessages,
	$setHasMoreMessages,
	$setIsFetchingMessages,
	$setMessages,
	type IMessage,
} from '@/entities/message.entity';
import {
	$clientsFilter,
	$clientsSearchText,
	$clientsSortDir,
	$messagesFetchKey,
	$messagesListScrollTop,
	$messagesSearchField,
	$messagesSortBy,
	$setMessagesFetchKey,
	$setMessagesListScrollTop,
	onlineStatusToIsOnline,
	toServerMessagesFilters,
} from '@/entities/clients-filter.entity';
import { isDevMode } from '@/lib/is-dev-mode';
import { mockGlobalMessages } from '@/mocks/fixtures';
import MessageCard from '../cards/message-card';

const LIMIT = 35;
const SCROLL_COMPENSATION_THRESHOLD = 80;

export default function MessagesList() {
	const listRef = useRef<HTMLDivElement | null>(null);
	const prevCountRef = useRef(0);
	const prevFirstIdRef = useRef<number | null>(null);

	const [messages, addMessages, setMessages] = useUnit([$messages, $addMessages, $setMessages]);
	const [hasMore, setHasMore] = useUnit([$hasMoreMessages, $setHasMoreMessages]);
	const [cursor, setCursor] = useUnit([$cursorMessages, $setCursorMessages]);
	const [isFetching, setIsFetching] = useUnit([$isFetchingMessages, $setIsFetchingMessages]);
	const [filter, searchText, searchField, sortBy, sortDir, savedScrollTop, setScrollTop, fetchKey, setFetchKey] = useUnit([
		$clientsFilter,
		$clientsSearchText,
		$messagesSearchField,
		$messagesSortBy,
		$clientsSortDir,
		$messagesListScrollTop,
		$setMessagesListScrollTop,
		$messagesFetchKey,
		$setMessagesFetchKey,
	]);
	const [firstFetched, setFirstFetched] = useState(isDevMode);

	const isOnline = onlineStatusToIsOnline(filter.onlineStatus);
	const serverFilters = useMemo(() => toServerMessagesFilters(filter), [filter]);
	const currentFetchKey = `${searchField}:${searchText}:${filter.onlineStatus}:${JSON.stringify(serverFilters)}:${sortBy}:${sortDir}`;

	const queryPayload = useMemo(
		() => ({
			limit: LIMIT,
			searchText: searchText || undefined,
			searchField,
			isOnline,
			filters: serverFilters,
			sortBy,
			sortDir,
		}),
		[searchText, searchField, isOnline, serverFilters, sortBy, sortDir],
	);

	const virtualizer = useVirtualizer({
		count: messages.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => 130,
		overscan: 8,
		measureElement: el => el.getBoundingClientRect().height,
		gap: 8,
	});
	const virtualItems = virtualizer.getVirtualItems();

	useVirtualListScrollRestore({
		listType: 'messages',
		listRef,
		virtualizer,
		items: messages,
		savedScrollTop,
		setScrollTop,
		ready: firstFetched && messages.length > 0,
	});

	useEffect(() => {
		if (isDevMode) {
			setMessages(mockGlobalMessages);
			setHasMore(false);
			return;
		}

		function addMessagesHandler(data: { messages: IMessage[]; cursor: string; hasMore: boolean }) {
			setIsFetching(false);
			if (!firstFetched) setFirstFetched(true);
			if (data?.messages?.length) {
				addMessages(data.messages);
				setCursor(data.cursor);
			}
			setHasMore(data.hasMore);
		}

		socket.on(SOCKET_EVENTS.addMessages, addMessagesHandler);

		return () => {
			socket.off(SOCKET_EVENTS.addMessages, addMessagesHandler);
		};
	}, []);

	useEffect(() => {
		if (isDevMode) {
			setFirstFetched(true);
			return;
		}

		function fetch() {
			setIsFetching(true);
			setMessages([]);
			setCursor(null);
			setScrollTop(0);
			setFetchKey(currentFetchKey);
			socket.emit(SOCKET_EVENTS.getMessages, { ...queryPayload, cursor: null });
		}

		if (messages.length > 0 && fetchKey === currentFetchKey) {
			setFirstFetched(true);
			return;
		}

		if (!firstFetched) {
			fetch();
		} else {
			const timeout = setTimeout(fetch, 250);
			return () => clearTimeout(timeout);
		}
	}, [currentFetchKey]);

	useLayoutEffect(() => {
		const firstId = messages[0]?.id ?? null;
		const isPrepend =
			messages.length === prevCountRef.current + 1 && prevCountRef.current > 0 && firstId !== prevFirstIdRef.current;

		if (isPrepend && listRef.current) {
			const el = listRef.current.querySelector('[data-index="0"]') as HTMLElement | null;
			const height = el?.getBoundingClientRect().height ?? 130;

			if (listRef.current.scrollTop > SCROLL_COMPENSATION_THRESHOLD) {
				listRef.current.scrollTop += height + 8;
			}
		}

		prevCountRef.current = messages.length;
		prevFirstIdRef.current = firstId;
	}, [messages]);

	useEffect(() => {
		if (!hasMore || isFetching) return;

		if (virtualItems.length > 0) {
			const lastItem = virtualItems[virtualItems.length - 1];
			if (lastItem.index >= messages.length - 10) {
				if (socket.connected) {
					setIsFetching(true);
					socket.emit(SOCKET_EVENTS.getMessages, { ...queryPayload, cursor });
				}
			}
		}
	}, [virtualItems, messages.length, isFetching, hasMore, cursor, queryPayload]);

	return (
		<div ref={listRef} data-scroll-container className='h-full overflow-auto pr-1 px-1'>
			{!firstFetched || (isFetching && !messages.length) ? (
				<div className='flex items-center justify-center mt-30'>
					<LogoLoader />
				</div>
			) : messages.length === 0 ? (
				<div className='text-center mt-30 text-md'>Сообщения не найдены</div>
			) : (
				<div
					style={{
						height: `${virtualizer.getTotalSize()}px`,
						width: '100%',
						position: 'relative',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
						}}
					>
						{virtualItems.map(({ index, key }) => {
							const message = messages[index];
							return (
								<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
									<MessageCard message={message} />
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
